/**
 * BusinessSubscriptionCard — la card de suscripción Business dentro de la app.
 *
 * Es el diseño de `temp_docs/CardBusiness.html`, que tiene dos variantes con
 * copys DISTINTOS (ver `copy.js`):
 *
 *  - `single` — el usuario tiene una sola ficha activa. Sin −/+: dice para qué
 *    ficha es («Para: TAPSTAR®») y cotiza un local.
 *  - `multi`  — tiene dos o más. El stepper arranca en su número de fichas, que
 *    es también el máximo («De tus 3 locales conectados»), y debajo va el
 *    desglose del ahorro por volumen.
 *
 * Los textos siguen al número ELEGIDO, no a la variante: si en `multi` se baja a
 * 1 con el stepper, las viñetas pasan a las de un local, como en el diseño.
 *
 * Una sola estructura para los dos tamaños del diseño:
 *  - Móvil (< lg): una columna con scroll y un pie fijo con el total (en
 *    `multi`), el CTA y la letra pequeña. Dentro de `PricingModal` es un bottom
 *    sheet; montada en una página, el pie queda simplemente al final.
 *  - Ordenador (lg+): dos columnas. A la izquierda, sobre crema, el título, las
 *    viñetas y «Todo lo que incluye» siempre abierto; a la derecha, la compra.
 *    El total entra en la caja del desglose.
 * Lo que cambia de sitio entre los dos se pinta dos veces con `lg:hidden` /
 * `hidden lg:*`, en vez de mover nodos con JS: así no hay salto al redimensionar.
 *
 * Componente PURO: el contenedor (`UpgradeCard`) resuelve el plan, la ficha y el
 * checkout. Los importes salen siempre de `/billing/active-pricing`.
 */
import { useId, useState } from 'react';
import { Loader2 } from 'lucide-react';

import CtaLink from '@/components/CtaLink';
import { useLanguage } from '@/components/LanguageContext';
import { cartBreakdown, localCurrencyCode } from '@/lib/priceBreakdown';
import { GROUPS, HIGHLIGHTS } from './copy';
import {
  ACCENT, BoxTotal, BreakdownRow, CheckIcon, ChevronIcon, CloseIcon, PeriodToggle,
  RichText, SeatsStepper, StoreIcon, useMoney,
} from './parts';

/**
 * @param {object} props
 * @param {any} props.pricing                Resultado de `getActivePricing()`.
 * @param {'single'|'multi'} props.variant
 * @param {number} props.seats
 * @param {number} props.maxSeats            Tope del stepper (todas las fichas, activas o no).
 * @param {number} [props.connectedCount]    Para «De tus N locales conectados».
 * @param {string} [props.locationName]      Para «Para: …» en `single`.
 * @param {'monthly'|'annual'} props.billing
 * @param {(n: number) => void} props.onSeatsChange
 * @param {(b: 'monthly'|'annual') => void} props.onBillingChange
 * @param {() => void} props.onCheckout
 * @param {boolean} [props.checkoutLoading]
 * @param {number} [props.trialDays]
 * @param {boolean} [props.trialAvailable]
 * @param {string} [props.ctaId]             Id del CTA del botón de pago.
 * @param {() => void} [props.onClose]       Pinta la X (solo dentro de un modal).
 * @param {string} [props.nota]              Por qué se ha abierto (cap alcanzado…).
 * @param {any} [props.enterpriseLink]    El enlace a Enterprise, cuando el
 *   usuario tiene más fichas de las que cubre Business.
 */
export default function BusinessSubscriptionCard({
  pricing, variant, seats, maxSeats, connectedCount = 0, locationName = '',
  billing, onSeatsChange, onBillingChange, onCheckout, checkoutLoading = false,
  trialDays = 0, trialAvailable = false, ctaId, onClose, nota = '', enterpriseLink = null,
}) {
  const { t, language } = useLanguage();
  const { money, hero, vat } = useMoney(pricing, language);
  const [showAll, setShowAll] = useState(false);
  const titleId = useId();
  const seatsLabelId = `${titleId}-seats`;

  const multi = variant === 'multi';
  const annual = billing === 'annual';
  const b = cartBreakdown(pricing, billing, multi ? seats : 1);
  const n = b.seats;
  // Los textos siguen al número elegido (ver cabecera).
  const copy = n > 1 ? 'multi' : 'single';
  const showTrial = trialAvailable && trialDays > 0;
  const localCurrency = localCurrencyCode(pricing?.displayCurrency);

  const period = t(annual ? 'billing.sub.perYear' : 'billing.sub.perMonth');
  const seatWord = t(n === 1 ? 'billing.sub.seatOne' : 'billing.sub.seatMany');
  const pct = pricing?.annualDiscountPct ?? 20;

  // Línea bajo el número grande.
  let secondary = null;
  if (multi) {
    if (annual) secondary = t('billing.sub.equivPerSeat', { month: money(b.unit / 12) });
  } else if (annual) {
    const yearVat = vat(b.unit);
    secondary = yearVat
      ? t('billing.sub.equivMonthVatYear', { month: money(b.unit / 12), yearVat })
      : t('billing.sub.equivMonth', { month: money(b.unit / 12) });
  } else {
    const monthVat = vat(b.unit);
    if (monthVat) secondary = t('billing.sub.vatMonth', { price: monthVat });
  }

  const totalVat = vat(b.total);
  const totalVatLine = totalVat
    ? t(annual ? 'billing.sub.vatYear' : 'billing.sub.vatMonth', { price: totalVat })
    : null;
  const totalSuffix = t(annual ? 'billing.sub.plusVatYear' : 'billing.sub.plusVatMonth');

  const toggle = (
    <PeriodToggle
      billing={billing}
      onChange={onBillingChange}
      monthlyLabel={t('billing.sub.monthly')}
      annualLabel={t('billing.sub.annual')}
      saveLabel={t('billing.sub.annualSave', { pct })}
      bestLabel={t('billing.sub.bestOption')}
      heightClass={multi ? 'min-h-[46px] lg:min-h-[48px]' : 'min-h-[52px] lg:min-h-[48px]'}
      stackOnMobile
      quietOff={!multi}
    />
  );

  const closeLabel = t('close');

  return (
    <section
      aria-labelledby={titleId}
      className={`relative flex min-h-0 w-full flex-1 flex-col overflow-hidden bg-white font-[Inter,system-ui,sans-serif] text-stone-900 lg:grid lg:grid-cols-[minmax(0,1fr)_400px] lg:grid-rows-[1fr_auto_auto_1fr] ${
        // Montada en una página lleva su propio marco; en el modal se lo pone él.
        onClose ? '' : 'rounded-3xl border border-stone-200 shadow-sm'
      }`}
    >
      {/* En móvil, todo menos el pie hace scroll; en ordenador este envoltorio
          desaparece (`lg:contents`) y sus dos hijos son celdas de la rejilla. */}
      <div
        className={`flex min-h-0 flex-1 flex-col overflow-y-auto px-5 lg:contents ${
          multi ? 'gap-3.5 pb-4 pt-[18px]' : 'gap-5 pb-5 pt-[22px]'
        }`}
      >
        {/* ── Columna izquierda: la promesa ─────────────────────────────── */}
        <div
          className={`flex flex-col lg:row-span-4 lg:min-h-0 lg:gap-5 lg:overflow-y-auto lg:bg-[#fffaf0] lg:px-9 lg:pb-8 lg:pt-9 ${
            multi ? 'gap-3.5' : 'gap-5'
          }`}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex flex-col gap-2">
              {nota && <p className="text-[13px] font-semibold leading-snug text-amber-800">{nota}</p>}
              <h2
                id={titleId}
                className={`m-0 font-extrabold leading-[1.2] tracking-[-0.02em] lg:text-[30px] lg:leading-[1.15] lg:tracking-[-0.025em] ${
                  multi ? 'text-[21px]' : 'text-[23px]'
                }`}
              >
                {showTrial
                  ? t('billing.sub.titleTrial', { days: trialDays })
                  : t('billing.sub.titleNoTrial')}
              </h2>
            </div>
            {onClose && (
              <button
                type="button"
                aria-label={closeLabel}
                onClick={onClose}
                className="-mr-2 -mt-2 flex h-11 w-11 shrink-0 cursor-pointer items-center justify-center rounded-full border-0 bg-stone-100 outline-none focus-visible:ring-2 focus-visible:ring-[#feae2c] lg:hidden"
              >
                <CloseIcon size={18} />
              </button>
            )}
          </div>

          <div className={`flex flex-col lg:gap-5 ${multi ? 'gap-1.5' : 'gap-2.5'}`}>
            <ul
              className={`m-0 flex list-none flex-col p-0 lg:gap-3.5 lg:text-base lg:leading-[1.45] ${
                multi ? 'gap-2 text-sm leading-[1.4]' : 'gap-3 text-[15px] leading-[1.4]'
              }`}
            >
              {HIGHLIGHTS[copy].map((k) => (
                <li key={k} className="flex items-start gap-3">
                  <CheckIcon className="mt-px lg:mt-0.5" />
                  <span><RichText i18nKey={k} /></span>
                </li>
              ))}
            </ul>

            {/* Móvil: las funciones van plegadas detrás de un botón. */}
            <button
              type="button"
              aria-expanded={showAll}
              onClick={() => setShowAll((v) => !v)}
              className="flex min-h-[44px] cursor-pointer items-center gap-1.5 self-start border-0 bg-transparent p-0 font-[inherit] text-sm font-semibold text-amber-800 lg:hidden"
            >
              <span>{t(showAll ? 'billing.sub.hideAll' : 'billing.sub.showAll')}</span>
              <ChevronIcon open={showAll} />
            </button>
            {showAll && (
              <div className="flex flex-col gap-4 rounded-2xl border border-stone-200 bg-stone-50 p-4 lg:hidden">
                {GROUPS[copy].map((g) => (
                  <div key={g.title} className="flex flex-col gap-2">
                    <h3 className="m-0 text-sm font-bold">{t(g.title)}</h3>
                    <ul className="m-0 flex list-disc flex-col gap-1.5 pl-[18px] text-sm leading-[1.4] text-stone-700">
                      {g.items.map((k) => <li key={k}>{t(k)}</li>)}
                    </ul>
                  </div>
                ))}
              </div>
            )}

            {/* Ordenador: siempre abiertas, en dos columnas. */}
            <div className="hidden flex-col gap-3 border-t border-[#f1e6d2] pt-[18px] lg:flex">
              <h3 className="m-0 text-sm font-bold text-stone-900">{t('billing.sub.allIncluded')}</h3>
              <div className="grid grid-cols-2 gap-x-5 gap-y-[18px] rounded-2xl border border-stone-200 bg-white p-[18px]">
                {GROUPS[copy].map((g) => (
                  <div key={g.title} className="flex flex-col gap-2">
                    <h3 className="m-0 text-[13px] font-bold text-stone-900">{t(g.title)}</h3>
                    <ul className="m-0 flex list-disc flex-col gap-1.5 pl-4 text-[13px] leading-[1.45] text-stone-700">
                      {g.items.map((k) => <li key={k}>{t(k)}</li>)}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ── Columna derecha: la compra ────────────────────────────────── */}
        <div
          className={`flex flex-col lg:col-start-2 lg:row-start-2 lg:gap-[18px] lg:px-9 lg:pt-10 ${
            multi ? 'gap-3.5' : 'gap-5'
          }`}
        >
          {onClose && (
            <button
              type="button"
              aria-label={closeLabel}
              onClick={onClose}
              className="absolute right-4 top-4 hidden h-10 w-10 cursor-pointer items-center justify-center rounded-full border-0 bg-stone-100 outline-none focus-visible:ring-2 focus-visible:ring-[#feae2c] lg:flex"
            >
              <CloseIcon size={16} />
            </button>
          )}
          <div className="hidden h-2 lg:block" />

          {/* En móvil, «Para:» va ANTES del toggle; en ordenador, después. */}
          <div className="order-2 lg:order-1">{toggle}</div>

          {!multi && locationName && (
            <div className="order-1 flex min-h-[52px] items-center justify-between gap-3 rounded-[14px] border border-stone-200 px-4 lg:order-2 lg:min-h-[48px] lg:rounded-xl lg:px-3.5">
              <div className="flex min-w-0 items-center gap-2.5 text-[15px]">
                <StoreIcon />
                <span className="text-stone-600">{t('billing.sub.forLabel')}</span>
                <span data-testid="sub-location-name" className="truncate font-semibold">{locationName}</span>
              </div>
            </div>
          )}

          {multi && (
            <div className="order-3 flex items-center justify-between gap-4">
              <div className="flex flex-col gap-0.5 leading-[1.2]">
                <span id={seatsLabelId} className="text-[15px] font-bold">{t('billing.sub.seatsQuestionApp')}</span>
                <span className="text-[13px] text-stone-600">
                  {t('billing.sub.connectedOf', { n: connectedCount })}
                </span>
              </div>
              <SeatsStepper
                n={n}
                max={maxSeats}
                onChange={onSeatsChange}
                labelledBy={seatsLabelId}
                decrementLabel={t('billing.sub.decrement')}
                incrementLabel={t('billing.sub.increment')}
              />
            </div>
          )}

          <div className="order-4 flex flex-col gap-1">
            <div className="flex flex-wrap items-baseline gap-2">
              <span
                data-testid="sub-hero-price"
                className={`font-extrabold leading-none tracking-[-0.03em] tabular-nums lg:text-[40px] ${
                  multi ? 'text-[34px]' : 'text-[40px]'
                }`}
              >
                {hero(b.unit)}
              </span>
              <span className="text-[15px] text-stone-600">
                {t('billing.sub.perSeatSuffix', { period })}
              </span>
            </div>
            {secondary && <div data-testid="sub-secondary" className="text-sm text-stone-600">{secondary}</div>}
            {localCurrency && (
              <div className="text-xs text-stone-500">
                {t('billing.card.localCurrencyNote', { currency: localCurrency })}
              </div>
            )}
          </div>

          {multi && (
            <div className="order-5 flex flex-col gap-[7px] rounded-[14px] border border-stone-200 bg-stone-50 px-3.5 py-3 tabular-nums lg:gap-2 lg:px-4 lg:py-3.5">
              {b.savings > 0 && (
                <div data-testid="sub-savings" className="text-[15px] font-bold text-green-700">
                  {t(annual ? 'billing.sub.savingsYear' : 'billing.sub.savingsMonth', {
                    amount: money(b.savings), pct: b.pct,
                  })}
                </div>
              )}
              <BreakdownRow
                label={t(annual ? 'billing.sub.grossYear' : 'billing.sub.grossMonth', {
                  n, seatWord, price: money(pricing?.totals?.monthly?.[0] || 0),
                })}
                value={money(b.gross)}
              />
              {annual && (
                <BreakdownRow
                  label={t('billing.sub.annualDiscount', { pct })}
                  value={`−${money(b.annualDiscount)}`}
                  discount
                />
              )}
              {b.volumeDiscount > 0 && (
                <BreakdownRow
                  label={t('billing.sub.volumeDiscount', { n, seatWord })}
                  value={`−${money(b.volumeDiscount)}`}
                  discount
                />
              )}
              <div className="hidden lg:block">
                <BoxTotal
                  label={t('billing.sub.total')}
                  total={money(b.total)}
                  suffix={totalSuffix}
                  vatLine={totalVatLine}
                />
              </div>
            </div>
          )}

          {enterpriseLink && <div className="order-6">{enterpriseLink}</div>}
        </div>
      </div>

      {/* ── Pie: total (móvil, multi), CTA y letra pequeña ─────────────── */}
      <div
        className={`flex shrink-0 flex-col gap-2.5 border-t border-stone-200 bg-white px-5 lg:col-start-2 lg:row-start-3 lg:gap-3 lg:border-t-0 lg:px-9 lg:pb-8 lg:pt-[22px] ${
          multi ? 'pb-4 pt-3' : 'pb-[22px] pt-3.5'
        }`}
      >
        {multi && (
          <div className="flex items-start justify-between gap-3 tabular-nums lg:hidden">
            <span className="pt-0.5 text-base font-bold">{t('billing.sub.total')}</span>
            <div className="flex flex-col items-end gap-px">
              <span className="whitespace-nowrap">
                <strong data-testid="sub-total-mobile" className="text-xl font-extrabold">{money(b.total)}</strong>{' '}
                <span className="text-sm text-stone-600">{totalSuffix}</span>
              </span>
              {totalVatLine && <span className="text-xs text-stone-600">{totalVatLine}</span>}
            </div>
          </div>
        )}
        <CtaLink
          as="button"
          cta={ctaId}
          type="button"
          onClick={onCheckout}
          disabled={checkoutLoading}
          style={{ background: ACCENT }}
          className={`inline-flex w-full cursor-pointer items-center justify-center gap-2 rounded-[14px] border-0 font-[inherit] text-[17px] font-bold text-stone-900 transition-[filter] hover:brightness-95 disabled:cursor-not-allowed disabled:opacity-60 lg:min-h-[54px] ${
            multi ? 'min-h-[52px]' : 'min-h-[56px]'
          }`}
        >
          {checkoutLoading && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
          {showTrial ? t('billing.sub.ctaTrial', { days: trialDays }) : t('billing.card.ctaNoTrial')}
        </CtaLink>
        <p className="m-0 text-center text-[13px] leading-normal text-stone-600">
          {showTrial ? t('billing.sub.fineTrial') : t('billing.card.footerNoTrial')}
        </p>
      </div>
    </section>
  );
}
